import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from 'app/services/translation.service';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface ErrorMessageMap {
  [key: string]: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorMessageService {
  private errorMessages: ErrorMessageMap[] = [];
  private isLoaded$ = new BehaviorSubject<boolean>(false);
  private loadingPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private translationService: TranslationService
  ) {
    this.loadErrorMessages();
  }

  private getLocaleFolder(): string {
    const lang =
      this.translationService.getCurrentLang() ||
      this.translate.currentLang ||
      '';

    if (lang === 'zh' || lang === 'zh_CN' || lang.startsWith('zh')) {
      return 'zh_CN';
    }
    return 'en_AU';
  }

  private async loadErrorMessages(): Promise<void> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise((resolve) => {
      const fileNames = ['email-verification', 'customer-form'];
      const locale = this.getLocaleFolder();
      const observables = fileNames.map((fileName) =>
        this.http
          .get<ErrorMessageMap>(
            `/assets/i18n/${locale}/Common/${fileName}.json`
          )
          .pipe(
            catchError((error) => {
              console.error('Error loading error messages:', error);
              return of({} as ErrorMessageMap);
            })
          )
      );

      forkJoin(observables).subscribe({
        next: (messages) => {
          this.errorMessages = messages;
          this.isLoaded$.next(true);
          resolve();
        },
        error: () => {
          this.isLoaded$.next(true);
          resolve();
        },
      });
    });

    return this.loadingPromise;
  }

  async getErrorMessage(messageCode: string): Promise<string> {
    try {
      await this.loadErrorMessages();
    } catch {
      return messageCode;
    }

    for (const messageMap of this.errorMessages) {
      if (messageMap[messageCode]) {
        return messageMap[messageCode];
      }
    }
    return messageCode;
  }
}
